import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ImageIcon, Store, Bell, BellRing, ShoppingCart, Zap, MessageCircle, Phone, ChevronLeft, Bike } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { Loader } from "../components/Loader";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, guest } = useAuth();
  const { addToCart } = useCart();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIndex, setImgIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [cities, setCities] = useState([]);
  const [isWatching, setIsWatching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/products/${id}`);
      setP(data);
      try { setCities(await api("/cities")); } catch {}
      try { const w = await api(`/products/${id}/watch`); setIsWatching(!!w.watching); } catch {}
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const requireLogin = () => { toast.error("يجب تسجيل الدخول أولاً"); navigate("/login"); };

  const colors = p?.colors || [];
  const sizes = p?.sizes || [];
  const discount = p?.discount_percent || 0;
  const hasDiscount = discount > 0;

  const images = useMemo(() => {
    if (!p) return [];
    if (selectedColor) {
      const c = colors.find((x) => x.name === selectedColor);
      if (c?.images_base64?.length) return c.images_base64;
    }
    return p.images_base64?.length ? p.images_base64 : (p.image_base64 ? [p.image_base64] : []);
  }, [p, selectedColor, colors]);
  useEffect(() => { setImgIndex(0); }, [selectedColor]);

  if (loading) return <Loader />;
  if (!p) return null;

  const isOwner = user?.user_id === p.seller_id;
  const canBuy = !isOwner && !guest && user?.role !== "seller" && p.quantity > 0;
  const finalPrice = hasDiscount ? p.price * (1 - discount / 100) : p.price;
  const needsColor = colors.length > 0 && !selectedColor;
  const needsSize = sizes.length > 0 && !selectedSize;
  const selectedSizeObj = sizes.find((s) => s.name === selectedSize);

  const validateVariant = () => {
    if (needsColor) { toast.error("اختر اللون المطلوب أولاً"); return false; }
    if (needsSize) { toast.error("اختر الحجم المطلوب أولاً"); return false; }
    if (selectedSizeObj && !selectedSizeObj.available) { toast.error("هذا الحجم غير متوفر"); return false; }
    return true;
  };

  const tryBuy = () => {
    if (!validateVariant()) return;
    const params = new URLSearchParams();
    if (selectedColor) params.set("color", selectedColor);
    if (selectedSize) params.set("size", selectedSize);
    const qs = params.toString();
    navigate(`/checkout/${p.product_id}${qs ? `?${qs}` : ""}`);
  };

  const tryAddToCart = async () => {
    if (guest) return requireLogin();
    if (!validateVariant()) return;
    try {
      await addToCart(p.product_id, 1, selectedColor || null, selectedSize || null);
      toast.success("تم إضافة المنتج للسلة");
    } catch (e) { toast.error(e.message || "تعذرت الإضافة"); }
  };

  const remove = async () => {
    if (!window.confirm("حذف المنتج؟")) return;
    try { await api(`/products/${id}`, { method: "DELETE" }); navigate(-1); }
    catch (e) { toast.error(e.message); }
  };

  const toggleWatch = async () => {
    if (guest) return requireLogin();
    try {
      const res = await api(`/products/${id}/watch`, { method: isWatching ? "DELETE" : "POST" });
      setIsWatching(!!res.watching);
      if (res.watching) toast.success("سنخبرك فور انخفاض سعر هذا المنتج 🔔");
    } catch (e) { toast.error(e.message); }
  };

  const openWhatsApp = () => {
    if (guest) return requireLogin();
    if (!p.seller_whatsapp) return toast.error("لا يوجد رقم واتساب");
    const num = p.seller_whatsapp.replace(/[^\d+]/g, "").replace("+", "");
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(`مرحبًا، أنا مهتم بالمنتج: ${p.name}`)}`, "_blank");
  };

  return (
    <div className="animate-fade-in-up max-w-5xl mx-auto">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square bg-[#F7F8FE] rounded-2xl overflow-hidden flex items-center justify-center">
            {images.length > 0 ? (
              <img src={images[imgIndex]} alt={p.name} className="w-full h-full object-cover" />
            ) : <ImageIcon size={80} className="text-[#9CA3AF]" />}
            {hasDiscount && <span className="absolute bottom-3 end-3 bg-[#00D26A] text-white font-black px-3 py-1.5 rounded-xl">خصم {discount}%</span>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {images.map((img, i) => (
                <button key={i} onClick={() => setImgIndex(i)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0 ${i === imgIndex ? "border-[#FF3D5A]" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 className="text-2xl font-black text-[#161629]">{p.name}</h1>
          <button onClick={() => navigate(`/seller/${p.seller_id}`)} className="flex items-center gap-2 text-[#6B7280] mt-2">
            <Store size={16} /> {p.seller_name}
          </button>
          <div className="flex items-center justify-between mt-4 p-4 bg-white rounded-2xl border border-[#E8EAF2]">
            <div className="flex items-center gap-3">
              <span className="text-3xl font-black text-[#FF3D5A]">{finalPrice.toFixed(2)} د.ل</span>
              {hasDiscount && <span className="text-lg text-[#9CA3AF] line-through font-bold">{p.price.toFixed(2)}</span>}
            </div>
            <span className={`font-bold ${p.quantity === 0 ? "text-[#FF3D5A]" : "text-[#00D26A]"}`}>{p.quantity === 0 ? "نفذت الكمية" : `متوفر: ${p.quantity}`}</span>
          </div>

          {colors.length > 0 && (
            <div className="mt-5">
              <p className="font-extrabold text-[#161629] mb-2">اللون: <span className="text-[#FF3D5A]">{selectedColor || "اختر لوناً"}</span></p>
              <div className="flex gap-2 flex-wrap">
                {colors.map((c) => {
                  const sel = selectedColor === c.name;
                  return (
                    <button key={c.name} data-testid={`color-pick-${c.name}`} onClick={() => setSelectedColor(sel ? null : c.name)} className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 font-bold text-sm ${sel ? "border-[#FF3D5A] bg-[#FFE5EA] text-[#FF3D5A]" : "border-[#E8EAF2] bg-white"}`}>
                      {c.hex && <span className="w-5 h-5 rounded-full border" style={{ backgroundColor: c.hex }} />}
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {sizes.length > 0 && (
            <div className="mt-4">
              <p className="font-extrabold text-[#161629] mb-2">الحجم: <span className="text-[#FF3D5A]">{selectedSize || "اختر حجماً"}</span></p>
              <div className="flex gap-2 flex-wrap">
                {sizes.map((s) => {
                  const sel = selectedSize === s.name;
                  return (
                    <button key={s.name} data-testid={`size-pick-${s.name}`} disabled={!s.available} onClick={() => setSelectedSize(sel ? null : s.name)} className={`min-w-[52px] px-4 py-2.5 rounded-xl border-2 font-black ${!s.available ? "border-[#FF3D5A] bg-[#FFE5EA] text-[#FF3D5A] line-through opacity-70" : sel ? "border-[#FF3D5A] bg-[#FF3D5A] text-white" : "border-[#E8EAF2] bg-white text-[#161629]"}`}>{s.name}</button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-sm font-bold text-[#6B7280] mt-6">الوصف</p>
          <p className="text-[#161629] mt-2 leading-7 whitespace-pre-wrap">{p.description || "لا يوجد وصف"}</p>

          {/* Delivery */}
          <div className="mt-5 p-4 bg-white rounded-2xl border border-[#E8EAF2]" data-testid="product-delivery-info">
            <div className="flex items-center gap-2 font-black text-[#161629]"><Bike size={18} className="text-[#FF3D5A]" /> التوصيل</div>
            {!p.delivery_type && <p className="text-sm text-[#6B7280] mt-2">لم يحدد البائع سعر التوصيل — تواصل مع المتجر للاستفسار</p>}
            {p.delivery_type === "free" && <p className="text-sm text-[#00D26A] mt-2">توصيل مجاني لجميع المدن 🎉</p>}
            {p.delivery_type === "flat" && <p className="text-sm text-[#6B7280] mt-2">سعر التوصيل: {Number(p.delivery_flat_price || 0).toFixed(2)} د.ل لجميع المدن</p>}
            {p.delivery_type === "distance" && <p className="text-sm text-[#6B7280] mt-2">التوصيل حسب المسافة (يُحسب عند الدفع)</p>}
            {p.delivery_type === "per_city" && (p.delivery_city_prices || []).map((cp) => {
              const name = cities.find((c) => c.city_id === cp.city_id)?.name || cp.city_id;
              return <div key={cp.city_id} className="flex justify-between py-1.5 border-b border-[#F0F2FA] text-sm"><span className="font-bold text-[#161629]">{name}</span><span className="font-extrabold text-[#FF3D5A]">{Number(cp.price) > 0 ? `${Number(cp.price).toFixed(2)} د.ل` : "مجاني"}</span></div>;
            })}
          </div>

          {!isOwner && (
            <button data-testid="watch-price-button" onClick={toggleWatch} className={`w-full flex items-center justify-center gap-2 mt-4 py-3 rounded-2xl border-2 font-black ${isWatching ? "bg-[#FF3D5A] border-[#FF3D5A] text-white" : "bg-white border-[#FF3D5A] text-[#FF3D5A]"}`}>
              {isWatching ? <BellRing size={18} /> : <Bell size={18} />} {isWatching ? "تراقب هذا المنتج — إلغاء" : "راقب السعر"}
            </button>
          )}

          <button data-testid="visit-seller-button" onClick={() => navigate(`/seller/${p.seller_id}`)} className="w-full flex items-center justify-center gap-2 mt-3 py-3 rounded-2xl bg-[#FFE5EA] text-[#FF3D5A] font-black">
            <Store size={18} /> زيارة صفحة المتجر <ChevronLeft size={18} />
          </button>

          {!isOwner && (
            <div className="flex gap-3 mt-4">
              <button data-testid="contact-whatsapp" onClick={openWhatsApp} className="flex-1 flex items-center justify-center gap-2 h-12 rounded-2xl text-white font-bold" style={{ backgroundColor: "#25D366" }}><MessageCircle size={20} /> واتساب</button>
              {p.seller_phone && <a data-testid="contact-phone" href={`tel:${p.seller_phone}`} className="flex-1 flex items-center justify-center gap-2 h-12 rounded-2xl text-white font-bold bg-[#3F8CFF]"><Phone size={20} /> اتصال</a>}
              <button data-testid="contact-chat" onClick={() => guest ? requireLogin() : navigate(`/chat/${p.seller_id}?name=${encodeURIComponent(p.seller_name)}&product=${p.product_id}`)} className="flex-1 flex items-center justify-center gap-2 h-12 rounded-2xl text-white font-bold bg-[#161629]"><MessageCircle size={20} /> رسالة</button>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6">
            {isOwner ? (
              <div className="flex gap-3">
                <button data-testid="delete-product-button" onClick={remove} className="flex-1 h-14 rounded-2xl bg-[#FFE5EA] text-[#FF3D5A] font-black">حذف</button>
                <button data-testid="promote-product-button" onClick={() => navigate(`/fund/${p.product_id}`)} className="flex-1 h-14 rounded-2xl bg-[#FF8A3C] text-white font-black">تمويل</button>
                <button data-testid="edit-product-button" onClick={() => navigate(`/edit-product/${p.product_id}`)} className="flex-1 h-14 rounded-2xl bg-[#FF3D5A] text-white font-black">تعديل</button>
              </div>
            ) : canBuy ? (
              <div className="flex gap-3">
                <button data-testid="add-to-cart-button" onClick={tryAddToCart} className="flex-1 flex items-center justify-center gap-2 h-14 rounded-2xl bg-[#7C5CFF] text-white font-black"><ShoppingCart size={20} /> أضف للسلة</button>
                <button data-testid="buy-now-button" onClick={tryBuy} className="flex-1 flex items-center justify-center gap-2 h-14 rounded-2xl bg-[#FF3D5A] text-white font-black"><Zap size={20} /> اشترِ الآن</button>
              </div>
            ) : guest && p.quantity > 0 ? (
              <button data-testid="guest-login-button" onClick={requireLogin} className="w-full h-14 rounded-2xl bg-[#FF3D5A] text-white font-black">سجّل للشراء</button>
            ) : (
              <div className="w-full h-14 rounded-2xl bg-[#F0F2FA] text-[#6B7280] font-black flex items-center justify-center">{p.quantity === 0 ? "غير متاح حاليًا" : "لا يمكنك الشراء"}</div>
            )}
          </div>
          {user?.is_admin && !isOwner && (
            <button data-testid="admin-delete-product" onClick={remove} className="w-full h-12 rounded-2xl bg-[#FFE5EA] text-[#FF3D5A] font-black mt-3">🗑️ حذف المنتج (إدارة)</button>
          )}
        </div>
      </div>
    </div>
  );
}
